<template>
  <div class="data-table">
    <a-table
      :columns="processedColumns"
      :data-source="dataSource"
      :loading="loading"
      :pagination="paginationConfig"
      :row-selection="rowSelectionConfig"
      :row-key="rowKey"
      :scroll="scroll"
      :size="size"
      :bordered="bordered"
      @change="handleTableChange"
    >
      <!-- ant-design-vue 3.x：使用 bodyCell 替代已废弃的 column.slots -->
      <template #bodyCell="{ text, record, index, column }">
        <template v-if="column.key === 'action' && hasActions">
          <TableActions
            :actions="actions!"
            :record="record"
            :index="index"
            @action-click="handleActionClick"
          />
        </template>
        <template v-else-if="customSlots.includes(column.key as string)">
          <slot :name="column.key as string" :text="text" :record="record" :index="index" />
        </template>
        <template v-else>
          {{ text }}
        </template>
      </template>
    </a-table>
  </div>
</template>

<script setup lang="ts">
import { computed, useSlots } from 'vue'
import type { TableColumnsType, TableProps, PaginationProps } from 'ant-design-vue'
import { useI18n } from 'vue-i18n'
import TableActions from './TableActions.vue'

const { t } = useI18n()

/**
 * 表格操作配置
 */
export interface TableAction {
  /** 操作键 */
  key: string
  /** 操作文本 */
  label: string
  /** 操作类型 */
  type?: 'link' | 'button' | 'danger' | 'primary'
  /** 是否显示（支持函数） */
  visible?: boolean | ((record: any) => boolean)
  /** 是否禁用（支持函数） */
  disabled?: boolean | ((record: any) => boolean)
  /** 确认提示（如果设置，点击时会弹出确认框） */
  confirm?: string
  /** 图标（Vue 组件） */
  icon?: any
}

interface Props {
  /** 表格列配置 */
  columns: TableColumnsType
  /** 数据源 */
  dataSource: any[]
  /** 加载状态 */
  loading?: boolean
  /** 分页配置 */
  pagination?: PaginationProps | false
  /** 行选择配置 */
  rowSelection?: TableProps['rowSelection']
  /** 行键 */
  rowKey?: string | ((record: any) => string)
  /** 滚动配置 */
  scroll?: { x?: number | string; y?: number | string }
  /** 表格尺寸 */
  size?: 'small' | 'middle' | 'large'
  /** 是否显示边框 */
  bordered?: boolean
  /** 操作列配置 */
  actions?: TableAction[]
}

const props = withDefaults(defineProps<Props>(), {
  loading: false,
  size: 'middle',
  bordered: false,
  rowKey: 'id'
})

const emit = defineEmits<{
  change: [pagination: any, filters: any, sorter: any]
  action: [action: string, record: any, index: number]
  'row-selection-change': [selectedRowKeys: any[], selectedRows: any[]]
}>()

const slots = useSlots()

// 处理后的列配置：去掉 column.slots（已废弃），需要自定义的列通过 bodyCell + 插槽名与 column.key 一致渲染
const processedColumns = computed(() => {
  const cols = (props.columns || []).map((col: any) => {
    const { slots: _s, ...rest } = col
    return rest
  })
  if (props.actions && props.actions.length > 0) {
    cols.push({
      title: t('common.actions'),
      key: 'action',
      fixed: 'right',
      width: props.actions.length * 80
    } as any)
  }
  return cols
})

// 自定义插槽列表
const customSlots = computed(() => {
  return Object.keys(slots).filter(slot => slot !== 'action')
})

// 是否有操作列
const hasActions = computed(() => {
  return props.actions && props.actions.length > 0
})

// 分页配置
const paginationConfig = computed(() => {
  if (props.pagination === false) {
    return false
  }
  
  return {
    current: 1,
    pageSize: 20,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total: number) => t('common.totalRecords', { total }),
    ...props.pagination
  }
})

// 行选择配置
const rowSelectionConfig = computed(() => {
  if (!props.rowSelection) {
    return undefined
  }
  
  return {
    ...props.rowSelection,
    onChange: (selectedRowKeys: any[], selectedRows: any[]) => {
      props.rowSelection?.onChange?.(selectedRowKeys, selectedRows)
      emit('row-selection-change', selectedRowKeys, selectedRows)
    }
  }
})

// 处理表格变化
const handleTableChange = (pagination: any, filters: any, sorter: any) => {
  emit('change', pagination, filters, sorter)
}

// 处理操作点击
const handleActionClick = (action: string, record: any, index: number) => {
  emit('action', action, record, index)
}
</script>

<style scoped lang="less">
.data-table {
  :deep(.ant-table-thead > tr > th) {
    background: #fafafa;
    font-weight: 600;
  }

  :deep(.ant-table-tbody > tr:hover > td) {
    background: #f5f5f5;
  }
}
</style>

